import React, { useMemo } from "react";
import { Card, Col } from "react-bootstrap";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DashboardAnalytics, DashboardPeriodLabel } from "../../types/dashboard";
import { getChartTitle } from "./dashboardCopy";

interface RevenueChartProps {
  analytics: DashboardAnalytics | null | undefined;
  period: DashboardPeriodLabel;
  selectedCinemaName: string | null;
}

const INDIAN_FORMAT = new Intl.NumberFormat("en-IN");

interface CompactTooltipProps {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string }>;
  label?: string;
}

const CompactTooltip: React.FC<CompactTooltipProps> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      style={{
        background: "#fff",
        padding: "10px 12px",
        border: "1px solid #ddd",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map((p) => (
        <div
          key={p.dataKey}
          style={{ color: p.color, fontWeight: 600, marginBottom: 2 }}
        >
          {p.dataKey === "revenue" ? `₹${INDIAN_FORMAT.format(p.value)}` : INDIAN_FORMAT.format(p.value)}
          {" "}
          <span style={{ color: "#666", fontWeight: 400 }}>
            {p.dataKey}
          </span>
        </div>
      ))}
    </div>
  );
};

const RevenueChart: React.FC<RevenueChartProps> = ({
  analytics,
  period,
  selectedCinemaName,
}) => {
  const revenueData = analytics?.revenueData ?? [];
  const title = getChartTitle(period, selectedCinemaName);

  const data = useMemo(() => revenueData, [revenueData]);

  return (
    <Col xs={12} className="mb-3">
      <Card>
        <Card.Header>
          <h5 className="mb-0">{title}</h5>
        </Card.Header>
        <Card.Body>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={data}
              margin={{ top: 10, right: 60, left: 100, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis
                yAxisId="left"
                tickFormatter={(value) => `₹${INDIAN_FORMAT.format(value)}`}
                label={{
                  value: "Revenue (₹)",
                  angle: -90,
                  position: "insideLeft",
                  offset: -50,
                  style: { fontSize: 12, fill: "#555" },
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tickFormatter={(value) => INDIAN_FORMAT.format(value)}
                label={{
                  value: "Bookings",
                  angle: 90,
                  position: "insideRight",
                  offset: -20,
                  style: { fontSize: 12, fill: "#555" },
                }}
              />
              <Tooltip content={<CompactTooltip />} />
              <Legend
                verticalAlign="top"
                height={36}
                iconType="line"
                wrapperStyle={{ fontSize: "0.85rem", fontWeight: 600 }}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="revenue"
                name="Revenue"
                stroke="#8884d8"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 8 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="bookings"
                name="Bookings"
                stroke="#82ca9d"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default RevenueChart;